import {
  MAX_SERVERS_PER_PLACEMENT_GROUP,
  type PlacementGroup,
  type Server,
} from "@cloudserver/contracts";
import { useReadOnly } from "../api/users.js";
import { Link } from "@tanstack/react-router";
import { Pencil, Plus, Split, Trash2, UserMinus, UserPlus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../api/client.js";
import { progressOf, useRunningOperations } from "../api/operations.js";
import {
  useAddToPlacementGroup,
  useCreatePlacementGroup,
  useDeletePlacementGroup,
  usePlacementGroups,
  useRemoveFromPlacementGroup,
  useUpdatePlacementGroup,
} from "../api/placement-groups.js";
import { useServers } from "../api/servers.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError, problemText } from "../ui/feedback.js";
import { relativeTime } from "../ui/format.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Dialog, type MenuItem } from "../ui/overlay.js";
import { Alert, Badge, Button, TextArea, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { formatLabels, parseLabels } from "./labels.js";

type T = ReturnType<typeof useTranslation>["t"];

const selectClass = "min-h-10 rounded-lg border border-line bg-surface px-3 text-sm";

/** Why a server cannot join this group now, or null when it can. */
function joinBlocker(t: T, server: Server, group: PlacementGroup, groups: PlacementGroup[]) {
  if (group.servers.some((s) => s.id === server.id)) return t("placementGroups.why.here");
  const other = groups.find((g) => g.servers.some((s) => s.id === server.id));
  if (other) return t("placementGroups.why.inGroup", { group: other.name });
  if (server.status !== "off") return t("placementGroups.why.running");
  return null;
}

/** The group a server belongs to, from the groups' member lists. */
export function groupOf(groups: PlacementGroup[] | undefined, serverId: string) {
  return groups?.find((g) => g.servers.some((s) => s.id === serverId));
}

function CreateDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreatePlacementGroup(projectId);
  const toast = useToast();
  const [name, setName] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      { name: name.trim(), kind: "spread" },
      {
        onSuccess: (group) => {
          toast({ tone: "ok", title: t("placementGroups.created", { name: group.name }) });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("placementGroups.create")}
      description={t("placementGroups.createLead")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="create-placement-group" busy={create.isPending}>
            {t("placementGroups.createSubmit")}
          </Button>
        </>
      }
    >
      <form id="create-placement-group" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("placementGroups.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={128}
          hint={t("placementGroups.nameHint")}
          error={fieldError(create.error, "name", t)}
        />
        <Alert tone="neutral">{t("placementGroups.intro")}</Alert>
        <ErrorMessage error={create.error} />
      </form>
    </Dialog>
  );
}

function MemberDialog({
  projectId,
  group,
  mode,
  servers,
  groups,
  onClose,
}: {
  projectId: string;
  group: PlacementGroup;
  mode: "add" | "remove";
  servers: Server[];
  groups: PlacementGroup[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const add = useAddToPlacementGroup(projectId);
  const remove = useRemoveFromPlacementGroup(projectId);
  const change = mode === "add" ? add : remove;
  const toast = useToast();
  const [serverId, setServerId] = useState("");
  const choices =
    mode === "add" ? servers : servers.filter((s) => group.servers.some((m) => m.id === s.id));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const server = servers.find((s) => s.id === serverId)?.name ?? "";
    change.mutate(
      { id: group.id, serverId },
      {
        onSuccess: () => {
          toast({
            tone: "ok",
            title: t(mode === "add" ? "placementGroups.adding" : "placementGroups.removing", {
              server,
              name: group.name,
            }),
          });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t(mode === "add" ? "placementGroups.addTitle" : "placementGroups.removeTitle", {
        name: group.name,
      })}
      description={
        mode === "add"
          ? t("placementGroups.addLead", { max: MAX_SERVERS_PER_PLACEMENT_GROUP })
          : t("placementGroups.removeLead")
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="placement-member"
            busy={change.isPending}
            disabled={!serverId}
          >
            {t(mode === "add" ? "placementGroups.add" : "placementGroups.remove")}
          </Button>
        </>
      }
    >
      <form id="placement-member" onSubmit={submit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">{t("placementGroups.server")}</span>
          <select
            value={serverId}
            onChange={(e) => setServerId(e.target.value)}
            className={selectClass}
            required
          >
            <option value="">{t("placementGroups.chooseServer")}</option>
            {choices.map((s) => {
              const why = mode === "add" ? joinBlocker(t, s, group, groups) : null;
              return (
                <option key={s.id} value={s.id} disabled={why !== null}>
                  {s.name} ({s.region}){why ? ` — ${why}` : ""}
                </option>
              );
            })}
          </select>
        </label>
        <ErrorMessage error={change.error} />
      </form>
    </Dialog>
  );
}

function EditDialog({
  projectId,
  group,
  onClose,
}: {
  projectId: string;
  group: PlacementGroup;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdatePlacementGroup(projectId);
  const [name, setName] = useState(group.name);
  const [labelsText, setLabelsText] = useState(formatLabels(group.labels));
  const [badLine, setBadLine] = useState<number | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseLabels(labelsText);
    setBadLine(parsed.badLine);
    if (!parsed.labels) return;
    update.mutate(
      { id: group.id, name: name.trim(), labels: parsed.labels },
      { onSuccess: onClose },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("placementGroups.editTitle", { name: group.name })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="edit-placement-group" busy={update.isPending}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="edit-placement-group" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("placementGroups.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={128}
          error={fieldError(update.error, "name", t)}
        />
        <TextArea
          label={t("placementGroups.labels")}
          value={labelsText}
          onChange={(e) => setLabelsText(e.target.value)}
          rows={3}
          spellCheck={false}
          placeholder="env=production"
          hint={t("placementGroups.labelsHint")}
          error={
            badLine !== null
              ? t("placementGroups.labelsInvalid", { line: badLine })
              : fieldError(update.error, "labels", t)
          }
        />
        <ErrorMessage error={update.error} />
      </form>
    </Dialog>
  );
}

type Open = { kind: "add" | "remove" | "edit" | "delete"; group: PlacementGroup } | null;

export function PlacementGroupsPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const groups = usePlacementGroups(projectId);
  const servers = useServers(projectId).data ?? [];
  const running = useRunningOperations(projectId);
  const remove = useDeletePlacementGroup(projectId);
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<Open>(null);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);
  const close = () => setOpen(null);

  const createButton = (
    <Button
      onClick={() => setCreating(true)}
      disabled={readOnly}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("placementGroups.create")}
    </Button>
  );

  const menu = (group: PlacementGroup): MenuItem[][] => {
    const busy = progressOf(running.data, group.id) !== null;
    const full = group.servers.length >= MAX_SERVERS_PER_PLACEMENT_GROUP;
    const empty = group.servers.length === 0;
    const why = (fallback: string) =>
      readOnly ? readOnlyReason : busy ? t("servers.busy") : fallback;
    return [
      [
        {
          label: t("placementGroups.add"),
          icon: <UserPlus className="size-4" aria-hidden />,
          disabled: readOnly || busy || full,
          reason: why(t("placementGroups.why.full", { max: MAX_SERVERS_PER_PLACEMENT_GROUP })),
          onSelect: () => setOpen({ kind: "add", group }),
        },
        {
          label: t("placementGroups.remove"),
          icon: <UserMinus className="size-4" aria-hidden />,
          disabled: readOnly || busy || empty,
          reason: why(t("placementGroups.why.empty")),
          onSelect: () => setOpen({ kind: "remove", group }),
        },
        {
          label: t("placementGroups.edit"),
          icon: <Pencil className="size-4" aria-hidden />,
          disabled: readOnly,
          reason: readOnlyReason,
          onSelect: () => setOpen({ kind: "edit", group }),
        },
      ],
      [
        {
          label: t("placementGroups.delete"),
          icon: <Trash2 className="size-4" aria-hidden />,
          danger: true,
          disabled: readOnly || busy || !empty,
          reason: why(t("placementGroups.why.notEmpty")),
          onSelect: () => setOpen({ kind: "delete", group }),
        },
      ],
    ];
  };

  const deleting = open?.kind === "delete" ? open.group : null;
  return (
    <>
      <PageHeader
        title={t("nav.placementGroups")}
        intro={t("placementGroups.intro")}
        help="placement-groups"
        actions={groups.data?.length ? createButton : undefined}
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {groups.error ? (
        <ErrorMessage error={groups.error} />
      ) : (
        <DataTable<PlacementGroup>
          label={t("nav.placementGroups")}
          rows={groups.data}
          rowKey={(g) => g.id}
          initialSort={{ key: "name", dir: "asc" }}
          progress={(g) => progressOf(running.data, g.id)}
          empty={
            <EmptyState
              icon={<Split className="size-6" aria-hidden />}
              title={t("placementGroups.empty")}
              action={createButton}
              help="placement-groups"
            >
              {t("placementGroups.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("placementGroups.name"),
              primary: true,
              sort: (g) => g.name,
              cell: (g) => <span className="break-all font-medium">{g.name}</span>,
            },
            {
              key: "kind",
              header: t("placementGroups.kind"),
              cell: () => <Badge>spread</Badge>,
            },
            {
              key: "servers",
              header: t("placementGroups.servers"),
              sort: (g) => g.servers.length,
              cell: (g) => (
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-xs text-muted">
                    {t("placementGroups.count", {
                      count: g.servers.length,
                      max: MAX_SERVERS_PER_PLACEMENT_GROUP,
                    })}
                  </span>
                  {g.servers.length === 0 ? (
                    <span className="text-muted">{t("placementGroups.noServers")}</span>
                  ) : (
                    <span className="flex flex-wrap gap-x-2">
                      {g.servers.map((s) =>
                        s.id ? (
                          <Link
                            key={s.id}
                            to="/projects/$projectId/servers/$serverId"
                            params={{ projectId, serverId: s.id }}
                            className="break-all text-accent hover:underline"
                          >
                            {s.name}
                          </Link>
                        ) : (
                          <span key={s.name} className="break-all">
                            {s.name}
                          </span>
                        ),
                      )}
                    </span>
                  )}
                </span>
              ),
            },
            {
              key: "labels",
              header: t("placementGroups.labels"),
              hideOnPhone: true,
              cell: (g) =>
                Object.keys(g.labels).length > 0 ? (
                  <span className="break-all font-mono text-xs">
                    {formatLabels(g.labels).replaceAll("\n", ", ")}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                ),
            },
            {
              key: "price",
              header: t("placementGroups.price"),
              align: "right",
              cell: () => <span className="text-muted">{t("placementGroups.free")}</span>,
            },
            {
              key: "created",
              header: t("placementGroups.createdAt"),
              hideOnPhone: true,
              sort: (g) => g.createdAt,
              cell: (g) => (
                <span className="text-muted">{relativeTime(g.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={menu}
        />
      )}
      {creating && <CreateDialog projectId={projectId} onClose={() => setCreating(false)} />}
      {(open?.kind === "add" || open?.kind === "remove") && (
        <MemberDialog
          projectId={projectId}
          group={open.group}
          mode={open.kind}
          servers={servers}
          groups={groups.data ?? []}
          onClose={close}
        />
      )}
      {open?.kind === "edit" && (
        <EditDialog projectId={projectId} group={open.group} onClose={close} />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && close()}
        title={t("placementGroups.deleteTitle", { name: deleting?.name ?? "" })}
        consequences={[t("placementGroups.deleteLead")]}
        actionLabel={t("placementGroups.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("placementGroups.deleted", { name: deleting.name }) });
              close();
            },
            onError: (error) =>
              toast({
                tone: "danger",
                title:
                  error instanceof ApiProblem && error.problem
                    ? problemText(t, error.problem)
                    : t("common.unexpected"),
              }),
          })
        }
      />
    </>
  );
}
